import {
  SSOAttributeRule,
  SSOAttributeRuleCondition,
  SSOAttributeRuleConditionOperator,
  SSOAttributeRuleType,
  SSOOptionsType,
} from '@/types/registration.types';
import {
  getDefaultSSOAttributeRuleCondition,
  getCustomAttributesFromSSO,
  isValidAttributeRule,
} from '@/utils/authentication/sso-utils';
import InputWithOverlay from '@ui/input-with-overlay/input-with-overlay';
import Modal from '@ui/modal/modal';
import { useEffect, useMemo, useState } from 'react';

import styles from './sso-attribute-rule-modal.module.css';
import Button from '@ui/buttons/button/button';
import SwitchV2 from '@ui/switch-v2/switch-v2';
import { ICONS } from '@ui/icons/types';
import Icon from '@ui/icons/icon';
import Text from '@ui/text/text/text';
import Container from '@ui/container/container';
import CustomReactSelect from '@ui/select/react-select/custom-react-select';
import { reactSelectAdminStyles } from '@ui/select/react-select/react-select-styles';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import useComputedRegistrationSettings from '../../../hooks/use-computed-registration-settings';
import { saveWorkingProjectByFields } from '@/store/slices/project';
import toastTrigger from '@ui/toast/toast-trigger';
import { getUser } from '@/store/slices/user/selectors';
import useOnFinishSave from '@/utils/use-on-finish-save';

interface SSOAttributeRuleModalProps {
  open: boolean;
  onClose: () => void;
  existingRule: SSOAttributeRule;
  selectedCustomSSO: SSOOptionsType;
}

const operatorOptions = [
  { label: 'Equals', value: SSOAttributeRuleConditionOperator.EQUALS },
  { label: 'Not Equals', value: SSOAttributeRuleConditionOperator.NOT_EQUALS },
  { label: 'Contains', value: SSOAttributeRuleConditionOperator.CONTAINS },
  { label: 'Not Contains', value: SSOAttributeRuleConditionOperator.NOT_CONTAINS },
  { label: 'Boolean True', value: SSOAttributeRuleConditionOperator.BOOLEAN_TRUE },
  { label: 'Boolean False', value: SSOAttributeRuleConditionOperator.BOOLEAN_FALSE },
];

const booleanOperators = [
  SSOAttributeRuleConditionOperator.BOOLEAN_TRUE,
  SSOAttributeRuleConditionOperator.BOOLEAN_FALSE,
];

const SAVE_ID = 'sso-attribute-rule-modal';

export default function SSOAttributeRuleModal({
  open,
  onClose,
  existingRule,
  selectedCustomSSO,
}: SSOAttributeRuleModalProps) {
  const dispatch = useAppDispatch();
  const user = useAppSelector(getUser);
  const { registrationSettings } = useComputedRegistrationSettings();
  const [rule, setRule] = useState<SSOAttributeRule>(existingRule);

  const handleClose = () => {
    onClose();
  };

  const { saving } = useOnFinishSave({
    saveId: SAVE_ID,
    onSuccess: () => {
      handleClose();
    },
    onFailure: (error: string) => {
      console.error(error);
      toastTrigger('Error saving attribute rule', {
        type: 'error',
      });
    },
  });

  const attributes = useMemo(() => {
    return getCustomAttributesFromSSO(selectedCustomSSO);
  }, [selectedCustomSSO]);

  useEffect(() => {
    setRule(existingRule);
  }, [existingRule]);

  const attributeOptions = useMemo(() => {
    if (attributes.length > 0) {
      return attributes.map((attribute) => ({ label: attribute, value: attribute }));
    } else {
      return [
        { label: 'No attributes available. Configure atribute mapping first.', isDisabled: true, value: 'error' },
      ];
    }
  }, [attributes]);

  const handleSave = () => {
    try {
      if (!isValidAttributeRule(rule, selectedCustomSSO) || !registrationSettings?.singleSignOn || !user) {
        return;
      }
      const existingRules: SSOAttributeRule[] = registrationSettings.singleSignOn?.rules ?? ([] as SSOAttributeRule[]);
      const nowDateString = new Date().toISOString();
      const updatedRule: SSOAttributeRule = { ...rule, updatedBy: user.id, updatedAt: nowDateString };

      const ruleIndex = existingRules.findIndex((r) => r.uuid === updatedRule.uuid);
      let updatedRules: SSOAttributeRule[];
      if (ruleIndex >= 0) {
        // Update existing rule
        updatedRules = existingRules.map((r, i) => (i === ruleIndex ? updatedRule : r));
      } else {
        // Add new rule
        updatedRules = [...existingRules, updatedRule];
      }

      const updatedSingleSignOn = { ...registrationSettings.singleSignOn, rules: updatedRules };

      dispatch(
        saveWorkingProjectByFields({
          registration_settings: {
            ...registrationSettings,
            singleSignOn: updatedSingleSignOn,
          },
          errorMessageOverride: 'Error saving attribute rule',
          saveId: SAVE_ID,
        })
      );
    } catch (error) {
      console.error(error);
      toastTrigger('Error saving attribute rule', {
        type: 'error',
      });
    }
  };

  const handleUpdateConditionValue = (index: number) => (value: string) => {
    setRule((prev) => ({ ...prev, conditions: prev.conditions.map((c, i) => (i === index ? { ...c, value } : c)) }));
  };

  const handleUpdateAttribute = (index: number) => (attribute: { label: string; value: string }) => {
    const newConditions = [...rule.conditions];
    const updatedCondition = { ...newConditions[index], attribute: attribute.value };
    newConditions[index] = updatedCondition;
    setRule({ ...rule, conditions: newConditions });
  };

  const handleUpdateOperator =
    (index: number) => (operator: { label: string; value: SSOAttributeRuleConditionOperator }) => {
      const newConditions = [...rule.conditions];
      const updatedCondition = { ...newConditions[index], operator: operator.value };
      newConditions[index] = updatedCondition;
      setRule({ ...rule, conditions: newConditions });
    };

  const handleRemoveCondition = (index: number) => {
    setRule({ ...rule, conditions: rule.conditions.filter((_, i) => i !== index) });
  };

  const renderConditionRow = (condition: SSOAttributeRuleCondition, index: number) => {
    const attributeExists = attributes.includes(condition.attribute);
    const conditionAttributeSetAndDoesNotExist = condition.attribute && !attributeExists;
    const containerClass = `${styles.attibuteConditionsRow} ${conditionAttributeSetAndDoesNotExist ? styles.error : ''}`;

    const isBooleanOperator = booleanOperators.includes(condition.operator);

    return (
      <Container
        design="GlobalCont3"
        borderRadius="12px"
        padding="16px"
        className={containerClass}
        key={index + condition.attribute}
      >
        {conditionAttributeSetAndDoesNotExist && (
          <div className={styles.warningContainer}>
            <Icon name={ICONS.CAUTION} size={16} />
            <Text>This attribute no longer exists</Text>
          </div>
        )}
        <CustomReactSelect
          value={attributeOptions.find((option) => option.value === condition.attribute)}
          placeholder="Select attribute"
          styles={reactSelectAdminStyles({
            option: (_, { isDisabled }) => {
              return {
                color: isDisabled ? 'var(--global-error-400)' : 'default',
              };
            },
          })}
          options={attributeOptions}
          onChange={(newValue: unknown) => handleUpdateAttribute(index)(newValue as { label: string; value: string })}
        />
        <CustomReactSelect
          value={operatorOptions.find((option) => option.value === condition.operator)}
          styles={reactSelectAdminStyles()}
          options={operatorOptions}
          onChange={(newValue: unknown) =>
            handleUpdateOperator(index)(newValue as { label: string; value: SSOAttributeRuleConditionOperator })
          }
        />

        {isBooleanOperator ? (
          <div></div>
        ) : (
          <InputWithOverlay
            inputHeight="40px"
            inputProps={{
              value: condition.value,
              placeholder: 'Enter value...',
              onChange: (e) => handleUpdateConditionValue(index)(e.target.value),
            }}
          />
        )}
        <Button clear onClick={() => handleRemoveCondition(index)}>
          <Icon name={ICONS.CLOSE} size={12} />
        </Button>
      </Container>
    );
  };

  return (
    <Modal
      open={open}
      onRequestClose={onClose}
      size="large"
      RenderHeaderContent={
        <InputWithOverlay
          wrapperClass="margin-b-16"
          inputProps={{
            value: rule.name,
            className: `${styles.ssoAttributeRuleModalInput} padding-h-8`,
            onChange: (e) => setRule({ ...rule, name: e.target.value }),
            placeholder: 'Add rule name...',
          }}
        />
      }
      RenderFooterContent={
        <div className="flex fd-row jc-flex-end ai-center gap-16">
          <Button design="LocalBttnSec" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            design="LocalBttnPrim"
            onClick={handleSave}
            loading={saving}
            disabled={!isValidAttributeRule(rule, selectedCustomSSO)}
          >
            Save
          </Button>
        </div>
      }
      contentWrapperClassName="padding-h-40 padding-v-24"
    >
      <div className="flex fd-column gap-16">
        <SwitchV2
          value={rule.type}
          on={rule.type === SSOAttributeRuleType.DENY}
          onLabel="Deny rule"
          offLabel="Allow rule"
          fullWidth={true}
          onClick={() =>
            setRule({
              ...rule,
              type: rule.type === SSOAttributeRuleType.DENY ? SSOAttributeRuleType.ALLOW : SSOAttributeRuleType.DENY,
            })
          }
        />
        <div className="flex fd-column gap-8">
          <Text className="S2">Attribute Conditions</Text>

          <div className="flex fd-column gap-8">{rule.conditions.map(renderConditionRow)}</div>
          <div>
            <Button
              design="LocalBttnSec"
              designSize="small"
              className="flex ai-center jc-center gap-6"
              onClick={() =>
                setRule({ ...rule, conditions: [...rule.conditions, getDefaultSSOAttributeRuleCondition()] })
              }
            >
              <Icon name={ICONS.ADD} size={12} />
              Add
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
